/**
 * The auth API surface the SPA calls.
 *
 * Spec 8.1: Better Auth owns `/api/auth/*` as REST, so these are plain fetches rather than
 * tRPC procedures. They are collected here so that every screen goes through one place
 * that knows about credentials, error shape, and the spec 22.2 code contract.
 */
import { ErrorCode, type Locale } from '@lila/shared';

const BASE = '/api/auth';

export type AuthFailure = {
  readonly code: ErrorCode;
  /** The provider's own message. Developer-facing only — the UI renders from `code`. */
  readonly rawMessage: string;
};

export class AuthError extends Error {
  readonly code: ErrorCode;

  constructor(failure: AuthFailure) {
    super(failure.rawMessage);
    this.name = 'AuthError';
    this.code = failure.code;
  }
}

/**
 * Better Auth reports failures with its own string codes. They are mapped onto
 * `@lila/shared`'s stable set here, at the boundary, so no screen ever switches on a
 * library-specific string.
 */
const CODE_MAP: Readonly<Record<string, ErrorCode>> = {
  INVALID_EMAIL_OR_PASSWORD: ErrorCode.INVALID_CREDENTIALS,
  INVALID_PASSWORD: ErrorCode.INVALID_CREDENTIALS,
  USER_NOT_FOUND: ErrorCode.INVALID_CREDENTIALS,
  CREDENTIAL_ACCOUNT_NOT_FOUND: ErrorCode.INVALID_CREDENTIALS,
  USER_ALREADY_EXISTS: ErrorCode.EMAIL_IN_USE,
  USER_EMAIL_NOT_VERIFIED: ErrorCode.EMAIL_NOT_VERIFIED,
  EMAIL_NOT_VERIFIED: ErrorCode.EMAIL_NOT_VERIFIED,
  INVALID_TOKEN: ErrorCode.TOKEN_EXPIRED,
  TOKEN_EXPIRED: ErrorCode.TOKEN_EXPIRED,
  PASSWORD_TOO_SHORT: ErrorCode.VALIDATION_ERROR,
  PASSWORD_TOO_LONG: ErrorCode.VALIDATION_ERROR,
  PASSWORD_COMPROMISED: ErrorCode.VALIDATION_ERROR,
};

function codeFromStatus(status: number): ErrorCode {
  if (status === 401) return ErrorCode.INVALID_CREDENTIALS;
  if (status === 403) return ErrorCode.FORBIDDEN;
  if (status === 409) return ErrorCode.EMAIL_IN_USE;
  if (status === 429) return ErrorCode.RATE_LIMITED;
  if (status >= 400 && status < 500) return ErrorCode.VALIDATION_ERROR;
  return ErrorCode.INTERNAL_ERROR;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    /* Hard rule 7: the session is an HttpOnly cookie; nothing is read from storage. */
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (response.ok) return (await response.json()) as T;

  const payload: unknown = await response.json().catch(() => null);
  const record =
    typeof payload === 'object' && payload !== null ? (payload as Record<string, unknown>) : {};

  const rawCode = typeof record['code'] === 'string' ? record['code'] : undefined;
  const rawMessage =
    typeof record['message'] === 'string' ? record['message'] : response.statusText;

  throw new AuthError({
    code:
      (rawCode === undefined ? undefined : CODE_MAP[rawCode]) ?? codeFromStatus(response.status),
    rawMessage,
  });
}

export type SignUpRequest = {
  email: string;
  password: string;
  displayName: string;
  locale: Locale;
};

export const authApi = {
  /* eslint-disable @typescript-eslint/explicit-module-boundary-types -- each member's
     return type is the `post<T>` type argument immediately beside it, so writing it twice
     would add noise without adding information. */
  signUp: ({ email, password, displayName }: SignUpRequest) =>
    /* Better Auth's field is `name`; ours is `displayName` (spec 7.4's column). */
    post<{ token: string }>('/sign-up/email', { email, password, name: displayName }),

  signIn: (input: { email: string; password: string; rememberMe: boolean }) =>
    post<{ token: string }>('/sign-in/email', input),

  signOut: () => post<unknown>('/sign-out', {}),

  /**
   * Spec 10.5: a guest joining by link "gets a real `users` row with `is_guest = true` ...
   * and a normal session cookie". ADR-0006 uses Better Auth's `anonymous()` plugin for it
   * rather than minting the row by hand, because session creation and cookie signing are
   * exactly the parts of an auth library worth having.
   *
   * Called by the join screen, and only when nobody is signed in — a person who already
   * has an account joins as themselves.
   */
  signInAsGuest: () => post<{ token: string }>('/sign-in/anonymous', {}),

  /**
   * Spec 10.3: "The response is identical whether or not the email exists (no account
   * enumeration)." The screen must therefore show the same confirmation regardless.
   */
  requestPasswordReset: (input: { email: string; redirectTo: string }) =>
    post<unknown>('/request-password-reset', input),

  resetPassword: (input: { token: string; newPassword: string }) =>
    post<unknown>('/reset-password', input),

  resendVerification: (input: { email: string; callbackURL: string }) =>
    post<unknown>('/send-verification-email', input),

  /**
   * Asks the server for the provider's authorisation URL, then leaves the SPA for it.
   *
   * It has to be a real navigation rather than a fetch: the provider renders its own
   * consent screen, and the PKCE/state cookie Better Auth sets alongside the URL is only
   * useful if the browser follows the redirect itself.
   */
  startOAuth: async (provider: 'google' | 'apple', callbackUrl: string): Promise<void> => {
    const { url } = await post<{ url: string }>('/sign-in/social', {
      provider,
      callbackURL: callbackUrl,
      errorCallbackURL: callbackUrl,
    });
    globalThis.location.assign(url);
  },
  /* eslint-enable @typescript-eslint/explicit-module-boundary-types */
};
